import Color from 'color';
import { experimental_extendTheme as extendTheme } from '@mui/material/styles';
import type { Breakpoint } from '@mui/material/styles';
import { generateTheme } from './generateTheme';

export type CustomColors = {
  black: string;
  white: string;
  light: string;
  dark: string;
  gray: string;
  vibrant: string;
  accent: string;
  neutral: string;
  transparentLight: string;
  transparentDark: string;
};

export type ColorValues = { [K in keyof CustomColors]: string };

// Define default colors
export const DEFAULT_COLORS: ColorValues = {
  black: '#000000',
  white: '#ffffff',
  light: '#f5f5f5',
  dark: '#121212',
  gray: '#9e9e9e',
  vibrant: '#00bcd4',
  accent: '#ff4081',
  neutral: '#cfd8dc',
  transparentLight: '#ffffff',
  transparentDark: '#ff0000'
};

// Utility function to validate if a value is a valid color
const isValidColor = (value: string): boolean => {
  try {
    Color(value);
    return true;
  } catch (e) {
    return false;
  }
};

const flattenPaletteToVars = (
  palette: Record<string, any>,
  prefix = 'palette'
): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  Object.entries(palette).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      const nested = flattenPaletteToVars(value, `${prefix}-${key}`);
      Object.assign(result, nested);
    } else {
      result[`--mui-${prefix}-${key}`] = value;
    }
  });
  return result;
};

export const getColorSchemeCssVars = (siteTheme: Partial<ColorValues> = {}): string => {
  // Merge provided colors with defaults
  const COLOR_SCHEMES: ColorValues = {
    ...DEFAULT_COLORS,
    ...siteTheme
  };

  const colorSchemeVars = Object.entries(COLOR_SCHEMES)
    .map(([key, value]) => `--mui-palette-${key}-main: ${value};`)
    .join('\n');

  const themeVars = Object.entries(COLOR_SCHEMES)
    .map(([scheme, hexColor]) => {
      const semColors = generateTheme(hexColor);

      const newTheme = extendTheme({
        colorSchemes: {
          light: {
            palette: {
              primary: semColors.primary,
              secondary: semColors.secondary,
              background: semColors.background,
              text: semColors.text
            }
          }
        },
        containerBreakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            xxl: 1920
          },
          up: (key: number | Breakpoint) =>
            `@media (min-width:${typeof key === 'number' ? key : key}px)`,
          down: (key: number | Breakpoint) =>
            `@media (max-width:${typeof key === 'number' ? key : key}px)`
        },
        cssVarPrefix: 'mui'
      });

      const flatVars = flattenPaletteToVars(newTheme.colorSchemes.light.palette);

      const cssString = Object.entries(flatVars)
        .filter((entry): entry is [string, string] => {
          const value = entry[1];
          return typeof value === 'string' && !!isValidColor(value);
        })
        .map(([key, value]) => `${key}: ${value};`)
        .join('');

      return `[data-color-scheme=${scheme}] {${cssString}background-color: var(--mui-palette-background-default);}`;
    })
    .join('');

  return `${colorSchemeVars}\n${themeVars}`;
};

export default getColorSchemeCssVars;
